import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PageBreadcrumb from '../../../shared/PageBreadCrumb';
import { EpcDmProjectDetailProvider, useDmProjectDetail } from '../manager/EpcDmProjectDetailContext';
import DeptStrip from '../manager/components/DeptStrip';
import { EpcProjectDetailProvider, parseRouteObjectId, useEpcProjectDetail } from './EpcProjectDetailContext';
import EpcProjectHeroCard from './EpcProjectHeroCard';

/**
 * Renders the department switcher for multi-department managers/dept-heads.
 * Lives inside the DM provider scope (so the DM context is available) and only
 * surfaces when the caller has 2+ org units — `myOrgUnits` is populated only for
 * manager/dept_head capabilities, and DeptStrip itself self-hides when < 2 items,
 * so PH / single-dept / site-engineer callers render nothing.
 */
function EpcProjectDeptStrip() {
  const { myOrgUnits, activeOrgUnitId, setActiveOrgUnit } = useDmProjectDetail();
  return <DeptStrip items={myOrgUnits} activeOrgUnitId={activeOrgUnitId} onChange={setActiveOrgUnit} />;
}

function EpcProjectDetailShell() {
  const { id: rawId } = useParams<{ id: string }>();
  const id = parseRouteObjectId(rawId);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { project, loading, hasSchedule, myCap, membershipsLoading } = useEpcProjectDetail();

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (!tab) return;
    const map: Record<string, string> = {
      overview: 'overview',
      schedule: 'schedule',
      imports: 'mpp',
      documents: 'documents',
      project3d: 'project-3d',
      'project-3d': 'project-3d',
    };
    const dest = map[tab];
    if (dest) {
      setSearchParams(
        (p) => {
          const n = new URLSearchParams(p);
          n.delete('tab');
          return n;
        },
        { replace: true }
      );
      navigate(dest, { replace: true, relative: 'path' });
    }
  }, [searchParams, setSearchParams, navigate]);

  /**
   * Site-engineer redirect: only site engineers landing on the project root get bounced to
   * their focused /workspace/pulse. PH / DH / DM stay on /epc/projects/:id/* and see their
   * role-aware tabs rendered inline by the hero card + nested routes.
   * Wait until memberships have resolved so we don't redirect prematurely.
   */
  useEffect(() => {
    if (membershipsLoading) return;
    if (myCap !== 'engineer') return;
    const path = location.pathname.replace(/\/+$/, '');
    if (!id) return;
    const projectRoot = `/epc/projects/${id}`;
    if (path === projectRoot) {
      navigate(`${projectRoot}/workspace/pulse`, { replace: true });
    }
  }, [membershipsLoading, myCap, location.pathname, id, navigate]);

  if (!id) {
    return (
      <div className="p-6">
        <p className="text-gray-700">Invalid or missing project id. Use the EPC Projects list to open a project.</p>
      </div>
    );
  }

  if (loading && !project) {
    return (
      <div className="p-6">
        <p className="text-gray-500">Loading…</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6">
        <p className="text-gray-500">Project not found.</p>
      </div>
    );
  }

  const projectCrumbLabel = `${project.code} — ${project.name}`;

  return (
    <div
      className="min-h-[calc(100vh-4rem)] px-3 py-4 sm:px-5 md:px-6"
      style={{ scrollbarGutter: 'stable' }}
    >
      <div className="mx-auto w-full max-w-[1600px] min-w-0">
        <PageBreadcrumb
          breadcrumbs={[
            { name: 'Home', path: '/' },
            { name: 'EPC Projects', path: '/epc/projects' },
            { name: projectCrumbLabel },
          ]}
        />
        <div
          className="mt-4 w-full min-w-0 overflow-hidden rounded-2xl"
          style={{
            background: '#FFFEFB',
            border: '1px solid rgba(11,59,46,0.16)',
            boxShadow: '0 1px 2px rgba(11,59,46,0.04), 0 18px 40px -24px rgba(11,59,46,0.20)',
          }}
        >
          <EpcProjectHeroCard project={project} hasSchedule={hasSchedule} />
          <EpcProjectDeptStrip />
          <div className="relative w-full min-w-0 overflow-x-clip p-4 sm:p-6 md:p-8">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EpcProjectDetailLayout() {
  return (
    <EpcProjectDetailProvider>
      <EpcDmProjectDetailProvider>
        <EpcProjectDetailShell />
      </EpcDmProjectDetailProvider>
    </EpcProjectDetailProvider>
  );
}
